import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Copy, Printer, Edit3, CheckCircle2, CheckCheck,
  FileText, ClipboardList, Monitor, PlusCircle, Save,
  XCircle, Pencil, AlertCircle, Loader2, Settings2, ChevronDown, ChevronUp,
  RotateCcw, Info, Eye, EyeOff, Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResources, type SavedResource } from './resources-context';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import { useProfile } from './ProfileContext';
import {
  generateActivitySheet,
  activityToText,
  type ActivityConfig,
  type GeneratedActivitySheet,
  type ActivitySection,
  type ActivityProblem,
} from './activity-engine';
import type { GeneratedLesson } from './lesson-engine';
import { lessonContentKey, extractBlueprint, validateBlueprintCompleteness } from './lesson-blueprint';
import { getWsHeight, REGISTRY_VERSION, getGradeBandConfig, getGradeBandOverrides, gradeNumFromLabel } from './rendering-registry';
import { runCertification, type CertificationReport } from './certification';
import { CertificationGate, CertificationWarningBanner } from './CertificationGate';
import { regenerateSectionAI } from './lesson-api';
import { type TeachBudThemeId, getTheme } from './theme-engine';
import { ThemePicker } from './ThemePicker';
import { BrandingPanel } from './BrandingPanel';
import { defaultBranding, hasBranding, type BrandingData } from './branding';
import { BrandingHeaderRow } from './BrandingHeaderRow';
import { useDefaultThemes } from './use-default-themes';
import { GenerationProgress } from './GenerationProgress';
import { DiagramScaffold } from './FlowDiagramSVG';
import { TenFrameSVG } from './representations/TenFrameSVG';
import { NumberBondSVG } from './representations/NumberBondSVG';
import { FractionStripSVG, parseFractionStripHint } from './representations/FractionStripSVG';
import { NumberLineSVG, parseNumberLineHint } from './representations/NumberLineSVG';
import { SequenceChainSVG } from './representations/SequenceChainSVG';
import { VennDiagramSVG, parseVennDiagramHint } from './representations/VennDiagramSVG';
import { CERorganizerSVG } from './representations/CERorganizerSVG';
import { TChartSVG, parseTChartHint } from './representations/TChartSVG';
import { StoryMapSVG, parseStoryMapHint } from './representations/StoryMapSVG';
import { MainIdeaWebSVG } from './representations/MainIdeaWebSVG';
import { SoundBoxesSVG, parseSoundBoxesHint } from './representations/SoundBoxesSVG';
import { PictureLetterCardSVG, parsePictureLetterCardHint } from './representations/PictureLetterCardSVG';
import { DiagramLabelEditor } from './DiagramLabelEditor';

// Maps activity-sheet display section names → lesson field keys used by the API
const SECTION_KEY_MAP: Record<string, string> = {
  'Challenge Problem':    'tieredPractice',
  'Warm-Up':             'warmUp',
  'Guided Practice':     'guidedPractice',
  'Independent Practice':'independentPractice',
  'Exit Ticket':         'exitTicket',
};

// ── Formatting preferences ─────────────────────────────────────────────────────

// FormatPreferences type and DEFAULT_FORMAT are defined in ./format-prefs.ts so
// activity-engine.ts can persist them without a circular import.
export type { FormatPreferences } from './format-prefs';
import { type FormatPreferences, DEFAULT_FORMAT } from './format-prefs';
import { applyPrintBypass } from './export-watermark';

// ── Formatting helpers ─────────────────────────────────────────────────────────

function toRoman(n: number): string {
  const vals = [10, 9, 5, 4, 1];
  const syms = ['x', 'ix', 'v', 'iv', 'i'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) { result += syms[i]; n -= vals[i]; }
  }
  return result;
}

function formatNumber(n: number, style: FormatPreferences['numberingStyle']): string {
  if (style === 'ABC') return String.fromCharCode(64 + n);
  if (style === 'roman') return toRoman(n);
  if (style === 'bullet') return '•';
  if (style === 'checkbox') return '☐';
  return String(n);
}

/**
 * Workspace heights (px) keyed by [workspaceSize][workspaceKind].
 *
 * 'lines' height is informational only — the renderer uses answerLinesCount × 32 px.
 * 'computation' and 'diagram' heights are used directly.
 *
 * Sizes are calibrated for real classroom use:
 *   computation — enough for multi-step math work shown by hand
 *   diagram     — enough for a hand-drawn sketch, number line, or area model
 */
// Workspace heights are now managed by the rendering registry.
// Use getWsHeight(kind, size) — imported from './rendering-registry'.
// Do not define heights here; add them to src/rendering-registry/registry.ts → WS_HEIGHTS_BY_KIND.

const LINE_SPACING_VAL: Record<FormatPreferences['lineSpacing'], string> = {
  compact: '1.3',
  normal: '1.55',
  relaxed: '1.8',
};

const FONT_COLORS: Record<FormatPreferences['fontColor'], { hex: string; label: string }> = {
  slate:  { hex: '#334155', label: 'Dark Gray' },
  navy:   { hex: '#1e3a5f', label: 'Navy'      },
  black:  { hex: '#0f172a', label: 'Black'     },
  forest: { hex: '#1a3a2a', label: 'Forest'    },
};

const Q_SPACING_CLASS: Record<FormatPreferences['questionSpacing'], string> = {
  tight: 'space-y-3',
  normal: 'space-y-5',
  generous: 'space-y-8',
};

// ── Toast ──────────────────────────────────────────────────────────────────────
interface Toast { id: number; message: string; type: 'success' | 'error' }

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  function show(message: string, type: Toast['type'] = 'success') {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }
  return { toasts, show };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 pointer-events-none print:hidden">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold text-white ${
              t.type === 'success' ? 'bg-slate-900' : 'bg-red-600'
            }`}>
            {t.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Toggle switch ──────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, id }: { checked: boolean; onChange: (v: boolean) => void; id: string }) {
  return (
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-primary' : 'bg-slate-200'
      }`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
  );
}

// ── Checkbox with label ────────────────────────────────────────────────────────
function CheckItem({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 py-2.5 cursor-pointer group">
      <div
        onClick={() => onChange(!checked)}
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          checked ? 'bg-primary border-primary' : 'border-slate-300 group-hover:border-primary/60'
        }`}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
          </svg>
        )}
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </label>
  );
}

// ── Formatting panel ───────────────────────────────────────────────────────────
function FormattingPanel({ prefs, onChange }: { prefs: FormatPreferences; onChange: (p: FormatPreferences) => void }) {
  function set<K extends keyof FormatPreferences>(key: K, value: FormatPreferences[K]) {
    onChange({ ...prefs, [key]: value });
  }

  type BtnProps = { active: boolean; onClick: () => void; children: React.ReactNode; mono?: boolean };
  function Btn({ active, onClick, children, mono }: BtnProps) {
    return (
      <button
        onClick={onClick}
        className={`px-2.5 py-1.5 rounded-lg text-xs border transition-all whitespace-nowrap ${
          active
            ? 'bg-primary text-white border-primary shadow-sm'
            : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40 hover:text-slate-800'
        } ${mono ? 'font-mono' : ''}`}
      >
        {children}
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 print:hidden"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5">

        {/* Font family */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Font</p>
          <div className="flex gap-1.5">
            <Btn active={prefs.fontFamily === 'sans'} onClick={() => set('fontFamily', 'sans')}>Sans</Btn>
            <Btn active={prefs.fontFamily === 'serif'} onClick={() => set('fontFamily', 'serif')}>
              <span style={{ fontFamily: 'Georgia, serif' }}>Serif</span>
            </Btn>
          </div>
        </div>

        {/* Font size */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Text Size</p>
          <div className="flex gap-1.5">
            {([13, 15, 17, 19] as const).map(size => (
              <Btn key={size} active={prefs.fontSize === size} onClick={() => set('fontSize', size)}>
                {size}
              </Btn>
            ))}
          </div>
        </div>

        {/* Font color */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Text Color</p>
          <div className="flex gap-2 items-center">
            {(Object.entries(FONT_COLORS) as [FormatPreferences['fontColor'], { hex: string; label: string }][]).map(([val, { hex, label }]) => (
              <button
                key={val}
                onClick={() => set('fontColor', val)}
                title={label}
                className={`w-7 h-7 rounded-full transition-all ${
                  prefs.fontColor === val
                    ? 'ring-2 ring-offset-1 ring-primary scale-110 shadow-md'
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
        </div>

        {/* Numbering style */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Numbering</p>
          <div className="flex flex-wrap gap-1.5">
            {([['123','1 2 3'],['ABC','A B C'],['roman','i ii'],['bullet','•'],['checkbox','☐']] as const).map(([val, label]) => (
              <Btn key={val} active={prefs.numberingStyle === val} onClick={() => set('numberingStyle', val)} mono={val === 'roman'}>
                {label}
              </Btn>
            ))}
          </div>
        </div>

        {/* Workspace size */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Student Workspace</p>
          <div className="flex flex-wrap gap-1.5">
            {([['none','Off'],['small','S'],['medium','M'],['large','L'],['xl','XL']] as const).map(([val, label]) => (
              <Btn key={val} active={prefs.workspaceSize === val} onClick={() => set('workspaceSize', val)}>
                {label}
              </Btn>
            ))}
          </div>
        </div>

        {/* Line spacing */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Line Spacing</p>
          <div className="flex gap-1.5">
            {([['compact','Compact'],['normal','Normal'],['relaxed','Open']] as const).map(([val, label]) => (
              <Btn key={val} active={prefs.lineSpacing === val} onClick={() => set('lineSpacing', val)}>{label}</Btn>
            ))}
          </div>
        </div>

        {/* Question spacing */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Question Spacing</p>
          <div className="flex gap-1.5">
            {([['tight','Tight'],['normal','Normal'],['generous','Wide']] as const).map(([val, label]) => (
              <Btn key={val} active={prefs.questionSpacing === val} onClick={() => set('questionSpacing', val)}>{label}</Btn>
            ))}
          </div>
        </div>

        {/* Page orientation */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Page Orientation</p>
          <div className="flex gap-1.5">
            <Btn active={prefs.orientation === 'portrait'} onClick={() => set('orientation', 'portrait')}>Portrait</Btn>
            <Btn active={prefs.orientation === 'landscape'} onClick={() => set('orientation', 'landscape')}>Landscape</Btn>
          </div>
        </div>

        {/* Graphics density */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Graphics</p>
          <div className="flex gap-1.5 flex-wrap">
            {([['none','None'],['minimal','Minimal'],['standard','Standard'],['rich','Rich']] as const).map(([val, label]) => (
              <Btn key={val} active={prefs.graphicsDensity === val} onClick={() => set('graphicsDensity', val)}>{label}</Btn>
            ))}
          </div>
        </div>

        {/* Page numbering */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Page Numbers</p>
          <div className="flex gap-1.5 flex-wrap">
            <Btn active={prefs.pageNumberStyle === 'none'} onClick={() => set('pageNumberStyle', 'none')}>Off</Btn>
            <Btn active={prefs.pageNumberStyle === 'page-x'} onClick={() => set('pageNumberStyle', 'page-x')}>Page X</Btn>
            <Btn active={prefs.pageNumberStyle === 'page-x-of-y'} onClick={() => set('pageNumberStyle', 'page-x-of-y')}>X of Y</Btn>
          </div>
          {prefs.pageNumberStyle !== 'none' && (
            <p className="text-[10px] text-slate-400 leading-snug">
              Page numbers print in Chrome and Edge. Firefox and Safari do not yet support CSS page-margin boxes.
            </p>
          )}
        </div>

        {/* Writing-space controls */}
        <div className="border-t border-slate-100 pt-4 space-y-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Writing Space</p>

          {/* Answer lines */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Answer Lines</span>
            <div className="flex items-center gap-2">
              <button onClick={() => set('answerLinesCount', Math.max(1, prefs.answerLinesCount - 1))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">−</button>
              <span className="w-5 text-center text-xs font-semibold text-slate-700">{prefs.answerLinesCount}</span>
              <button onClick={() => set('answerLinesCount', Math.min(10, prefs.answerLinesCount + 1))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">+</button>
            </div>
          </div>

          {/* Response box height */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Response Box</span>
            <div className="flex items-center gap-2">
              <button onClick={() => set('responseBoxHeight', Math.max(40, prefs.responseBoxHeight - 20))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">−</button>
              <span className="w-12 text-center text-xs font-semibold text-slate-700">{prefs.responseBoxHeight}px</span>
              <button onClick={() => set('responseBoxHeight', Math.min(300, prefs.responseBoxHeight + 20))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">+</button>
            </div>
          </div>

          {/* Diagram space */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Diagram Space</span>
            <div className="flex items-center gap-2">
              <button onClick={() => set('diagramSpaceHeight', Math.max(0, prefs.diagramSpaceHeight - 20))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">−</button>
              <span className="w-12 text-center text-xs font-semibold text-slate-700">{prefs.diagramSpaceHeight > 0 ? `${prefs.diagramSpaceHeight}px` : 'Off'}</span>
              <button onClick={() => set('diagramSpaceHeight', Math.min(300, prefs.diagramSpaceHeight + 20))}
                className="w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center text-sm font-bold">+</button>
            </div>
          </div>

          {/* Graph paper */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Graph Paper Grid</span>
            <button onClick={() => set('graphPaperAreas', !prefs.graphPaperAreas)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${prefs.graphPaperAreas ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
              {prefs.graphPaperAreas ? 'On' : 'Off'}
            </button>
          </div>

          {/* Manual page breaks */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Break Between Sections</span>
            <button onClick={() => set('manualPageBreaks', !prefs.manualPageBreaks)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${prefs.manualPageBreaks ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
              {prefs.manualPageBreaks ? 'On' : 'Off'}
            </button>
          </div>
        </div>

      </div>
    </motion.div>
  );
}

// ── Section accent map: section name → index in theme.sectionAccents ────────
const SECTION_ACCENT_INDEX: Record<string, number> = {
  'Warm-Up': 0, 'Vocabulary': 1, 'Direct Instruction': 2,
  'Guided Practice': 3, 'Independent Practice': 4, 'Challenge Problem': 5, 'Exit Ticket': 6,
};

// ── Section header visual config — colored band + icon (Spec §6.2) ────────────
// Each section gets a distinct background color and emoji icon for the header
// band. When a theme accent color is provided, it overrides the bg color so
// the whole sheet stays on-brand while keeping the icon for scannability.
const SECTION_HEADER_META: Record<string, { bg: string; text: string; icon: string }> = {
  'Warm-Up':              { bg: '#ea580c', text: '#fff', icon: '🔥' },
  'Vocabulary':           { bg: '#0d9488', text: '#fff', icon: '📚' },
  'Direct Instruction':   { bg: '#4f46e5', text: '#fff', icon: '📖' },
  'Guided Practice':      { bg: '#2563eb', text: '#fff', icon: '🤝' },
  'Independent Practice': { bg: '#7c3aed', text: '#fff', icon: '✏️' },
  'Challenge Problem':    { bg: '#059669', text: '#fff', icon: '⭐' },
  'Exit Ticket':          { bg: '#be185d', text: '#fff', icon: '📝' },
  'Homework':             { bg: '#6366f1', text: '#fff', icon: '🏠' },
};
const DEFAULT_SECTION_HEADER = { bg: '#64748b', text: '#fff', icon: '📄' };

// ── Default border colors kept for AnswerKeyCard (still uses left-border style)
const SECTION_COLORS: Record<string, string> = {
  'Warm-Up': 'border-l-amber-500',
  'Vocabulary': 'border-l-teal-500',
  'Direct Instruction': 'border-l-indigo-500',
  'Guided Practice': 'border-l-blue-500',
  'Independent Practice': 'border-l-violet-500',
  'Challenge Problem': 'border-l-emerald-500',
  'Exit Ticket': 'border-l-rose-600',
};

type RenderedItem =
  | { kind: 'group'; label: string }
  | { kind: 'problem'; problem: ActivityProblem };

function groupProblems(problems: ActivityProblem[]): RenderedItem[] {
  const items: RenderedItem[] = [];
  let lastGroup = '';
  for (const p of problems) {
    if (p.groupLabel && p.groupLabel !== lastGroup) {
      lastGroup = p.groupLabel;
      items.push({ kind: 'group', label: p.groupLabel });
    }
    items.push({ kind: 'problem', problem: p });
  }
  return items;
}

function SectionCard({
  section, prefs, themeAccentHex, isEditing, onUpdateSection, isHidden, onToggleHidden, gradeNum,
}: {
  section: ActivitySection;
  prefs: FormatPreferences;
  themeAccentHex?: string;
  isEditing?: boolean;
  onUpdateSection?: (updated: ActivitySection) => void;
  isHidden?: boolean;
  onToggleHidden?: () => void;
  /** Numeric grade: 0=K, 1-8. Controls grade-band typography and visual-dominant layout. */
  gradeNum?: number;
}) {
  const baseHeading = section.heading.replace(' (Foundational)', '').replace(' (Enrichment)', '');
  const headerMeta  = SECTION_HEADER_META[baseHeading] ?? DEFAULT_SECTION_HEADER;
  const bandBg      = themeAccentHex ?? headerMeta.bg;
  const bandText    = headerMeta.text;
  // Grade-band layout config: visual-dominant (K-1) + spec-aligned line height
  const gradeBand   = getGradeBandConfig(gradeNum ?? 4);
  const isVisualDominant = gradeBand.visualDominant;
  const workspaceBorder  = gradeBand.workspaceBorderPx;
  const fontFamily = prefs.fontFamily === 'serif' ? 'Georgia, "Times New Roman", serif' : 'var(--app-font-sans, system-ui, sans-serif)';
  const fontColor  = FONT_COLORS[prefs.fontColor ?? 'slate']?.hex ?? FONT_COLORS.slate.hex;
  const qSpacingClass = Q_SPACING_CLASS[prefs.questionSpacing];
  // wsHeights row now lives in the rendering registry; use getWsHeight(kind, size) per-problem below.
  // Use grade-band lineHeight for K-3 (spec 2.0/1.8), fall back to user pref otherwise
  const lh = (gradeNum !== undefined && gradeNum <= 3)
    ? gradeBand.lineHeight
    : LINE_SPACING_VAL[prefs.lineSpacing];
  const items = groupProblems(section.problems);
  const isVocab = baseHeading === 'Vocabulary';
  const showWorkspace = prefs.workspaceSize !== 'none' && !isVocab;

  const numberSuffix = (style: FormatPreferences['numberingStyle']) =>
    style === 'bullet' || style === 'checkbox' ? '' : '.';

  function setInstruction(val: string) {
    onUpdateSection?.({ ...section, instruction: val });
  }

  function setProblemPrompt(num: number, val: string) {
    onUpdateSection?.({
      ...section,
      problems: section.problems.map(p => p.number === num ? { ...p, prompt: val } : p),
    });
  }

  function setProblemWsProps(num: number, wsProps: Record<string, string>) {
    onUpdateSection?.({
      ...section,
      problems: section.problems.map(p => p.number === num ? { ...p, wsProps } : p),
    });
  }

  return (
    <div
      className={`as-sheet-section bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-opacity ${isHidden ? 'opacity-40 print:hidden' : ''} ${prefs.manualPageBreaks ? 'print:break-after-page' : ''}`}
    >
      {/* Section heading — full-width colored band with icon (Spec §6.2) */}
      <div
        className="px-5 py-3 flex items-center gap-3"
        style={{
          background: bandBg,
          // Ensure background colors print (not stripped to white)
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        } as React.CSSProperties}
      >
        <span className="text-lg leading-none flex-shrink-0" aria-hidden="true">{headerMeta.icon}</span>
        {isEditing && onUpdateSection ? (
          /* Editable heading — preserves sectionKind so color/routing survive the rename */
          <input
            type="text"
            aria-label="Section heading"
            value={section.heading}
            onChange={e => onUpdateSection({ ...section, heading: e.target.value })}
            style={{ fontSize: `${prefs.fontSize + (gradeBand.headingOffset)}px`, fontFamily, lineHeight: '1.3', color: bandText }}
            className="font-bold flex-1 bg-transparent border-b border-white/40 focus:outline-none focus:border-white/70 min-w-0 print:hidden"
          />
        ) : (
          <h3
            style={{ fontSize: `${prefs.fontSize + (gradeBand.headingOffset)}px`, fontFamily, color: bandText, lineHeight: '1.3' }}
            className="font-bold flex-1"
          >
            {section.heading}
            {isHidden && <span className="ml-2 text-xs font-medium rounded-full px-2 py-0.5 align-middle" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: bandText }}>Hidden</span>}
          </h3>
        )}
        {onToggleHidden && (
          <button onClick={onToggleHidden} title={isHidden ? 'Show section' : 'Hide section'}
            className="flex-shrink-0 p-1 rounded-lg hover:bg-white/20 transition-colors print:hidden"
            style={{ color: bandText }}>
            {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
      {/* Instruction line below the band */}
      <div className="px-6 pt-3 pb-0">
        {isEditing && onUpdateSection ? (
          <textarea
            value={section.instruction}
            onChange={e => setInstruction(e.target.value)}
            rows={2}
            style={{ fontSize: `${prefs.fontSize - 1}px`, fontFamily }}
            className="w-full px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-amber-300"
          />
        ) : (
          <p
            style={{ fontSize: `${prefs.fontSize - 1}px`, fontFamily }}
            className="text-slate-500 font-medium"
          >
            {section.instruction}
          </p>
        )}
      </div>

      {/* Problems */}
      <div className={`px-6 py-5 ${qSpacingClass}`}>
        {items.map((item, idx) => {
          if (item.kind === 'group') {
            return (
              <div
                key={`group-${idx}`}
                className="pt-2 pb-1.5 border-b-2 border-slate-200"
                style={{ marginBottom: '0.25rem' }}
              >
                <span
                  style={{ fontSize: `${prefs.fontSize + 1}px`, fontFamily }}
                  className="font-semibold text-slate-700"
                >
                  {item.label}
                </span>
              </div>
            );
          }

          const p = item.problem;
          const wsKind = p.workspaceKind ?? 'lines';
          const wsHeight = getWsHeight(wsKind, prefs.workspaceSize);

          // Detect writing-strategy labeled items (CLAIM: / EVIDENCE: / REASONING: etc.)
          const writingLabelMatch = p.prompt.match(
            /^(CLAIM|EVIDENCE|REASONING|RESTATE|ANSWER|CITE|EXPLAIN|SCENARIO|QUESTION|INTRODUCTION|BODY|CONCLUSION|ASSERT|SUPPORT|ANALYZE):\s*/i,
          );
          if (writingLabelMatch) {
            const label = writingLabelMatch[1].toUpperCase();
            const content = p.prompt.slice(writingLabelMatch[0].length).trim();
            return (
              <div key={p.number} className="space-y-2 py-1">
                <p
                  style={{ fontSize: `${prefs.fontSize - 1}px`, fontFamily }}
                  className="font-bold uppercase tracking-widest text-slate-500"
                >
                  {label}
                </p>
                {content && (
                  <p
                    style={{ fontSize: `${prefs.fontSize}px`, fontFamily, lineHeight: lh }}
                    className="text-slate-700"
                  >
                    {content}
                  </p>
                )}
                <div className="space-y-2 pt-1">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="border-b border-slate-300" style={{ height: '28px' }} aria-hidden="true" />
                  ))}
                </div>
              </div>
            );
          }

          // For K-1 visual-dominant mode, render the workspace BEFORE the prompt text
          // (Spec §1.1: visual is the primary anchor; text is secondary for grades K-1)
          const problemText = isEditing && onUpdateSection ? (
            <div className="flex gap-2 items-start">
              <span className="font-bold text-slate-400 select-none mt-2 flex-shrink-0" style={{ marginRight: '0.45em', fontSize: `${prefs.fontSize}px` }}>
                {formatNumber(p.number, prefs.numberingStyle)}{numberSuffix(prefs.numberingStyle)}
              </span>
              <textarea
                value={p.prompt}
                onChange={e => setProblemPrompt(p.number, e.target.value)}
                rows={2}
                style={{ fontSize: `${prefs.fontSize}px`, fontFamily, lineHeight: lh }}
                className="flex-1 px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-slate-800 resize-none focus:outline-none focus:ring-1 focus:ring-amber-300"
              />
            </div>
          ) : (
            <p
              style={{ fontSize: `${prefs.fontSize}px`, fontFamily, lineHeight: lh, color: fontColor }}
              className="whitespace-pre-line"
            >
              <span className="font-bold text-slate-400 select-none" style={{ marginRight: '0.45em' }}>
                {formatNumber(p.number, prefs.numberingStyle)}{numberSuffix(prefs.numberingStyle)}
              </span>
              {p.prompt}
            </p>
          );

          return (
            <div key={p.number} className={`space-y-2 ${isVisualDominant ? 'flex flex-col-reverse' : ''}`}>
              {!isVisualDominant && problemText}
              {showWorkspace && !isEditing && (
                <div className="space-y-1.5 pt-1" aria-hidden="true">
                  {wsKind === 'lines' ? (
                    /* Ruled answer lines */
                    Array.from({ length: prefs.answerLinesCount }).map((_, i) => (
                      <div key={i}
                        className="border-b border-slate-300"
                        style={{
                          height: '32px',
                          backgroundImage: prefs.graphPaperAreas
                            ? 'linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)'
                            : undefined,
                          backgroundSize: prefs.graphPaperAreas ? '16px 16px' : undefined,
                        }}
                      />
                    ))
                  ) : wsHeight > 0 ? (
                    wsKind === 'diagram' ? (
                      /* Organism / flow scaffold for "draw / draft a model" questions */
                      <DiagramScaffold prompt={p.prompt} height={wsHeight} />
                    ) : wsKind === 'ten-frame' ? (
                      /* Ten-frame — K–2 counting & addition */
                      <div className="flex flex-col items-center gap-2 py-2">
                        <TenFrameSVG studentMode width={Math.min(320, wsHeight * 2)} showLabel />
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Fill in the ten frame</p>
                      </div>
                    ) : wsKind === 'number-bond' ? (
                      /* Number bond — K–2 part-part-whole */
                      <div className="flex flex-col items-center gap-1 py-2">
                        <NumberBondSVG studentMode width={Math.min(220, wsHeight * 1.2)} />
                      </div>
                    ) : wsKind === 'fraction-strip' ? (
                      /* Fraction Strip — teacher-editable numerator/denominator via wsProps */
                      <div className="flex flex-col items-center gap-1 py-2">
                        {(() => { const base = parseFractionStripHint(p.prompt); const wp = p.wsProps ?? {}; return <FractionStripSVG {...base} numerator={wp.numerator !== undefined ? parseInt(wp.numerator, 10) : base.numerator} denominator={wp.denominator !== undefined ? parseInt(wp.denominator, 10) : base.denominator} studentMode width={Math.min(500, wsHeight * 3.2)} />; })()}
                      </div>
                    ) : wsKind === 'number-line' ? (
                      /* Number line — teacher-editable start/end/interval via wsProps */
                      <div className="flex flex-col items-center gap-1 py-3">
                        {(() => { const base = parseNumberLineHint(p.prompt); const wp = p.wsProps ?? {}; return <NumberLineSVG {...base} start={wp.start !== undefined ? parseFloat(wp.start) : base.start} end={wp.end !== undefined ? parseFloat(wp.end) : base.end} interval={wp.interval !== undefined ? parseFloat(wp.interval) : base.interval} studentMode width={Math.min(520, wsHeight * 5)} />; })()}
                      </div>
                    ) : wsKind === 'sequence-chain' ? (
                      /* Sequence chain — step labels teacher-editable via wsProps */
                      <div className="flex flex-col gap-1 py-2">
                        {(() => { const wp = p.wsProps ?? {}; const labels = ([0,1,2,3,4].map(i => wp[`stepLabel${i}`]).filter(Boolean) as string[]); return <SequenceChainSVG studentMode steps={4} stepLabels={labels.length > 0 ? labels : undefined} width={Math.min(580, wsHeight * 3.5)} />; })()}
                      </div>
                    ) : wsKind === 'venn-diagram' ? (
                      /* Venn diagram — circle/overlap labels teacher-editable via wsProps */
                      <div className="flex flex-col items-center gap-1 py-2">
                        {(() => { const base = parseVennDiagramHint(p.prompt); const wp = p.wsProps ?? {}; return <VennDiagramSVG {...base} leftLabel={wp.leftLabel || base.leftLabel} rightLabel={wp.rightLabel || base.rightLabel} bothLabel={wp.bothLabel || undefined} studentMode width={Math.min(520, wsHeight * 1.75)} />; })()}
                      </div>
                    ) : wsKind === 'cer-organizer' ? (
                      /* CER organizer — sentence starters teacher-editable via wsProps */
                      <div className="flex flex-col gap-1 py-2">
                        {(() => { const wp = p.wsProps ?? {}; return <CERorganizerSVG studentMode claimStarter={wp.claimStarter || undefined} evidenceStarter={wp.evidenceStarter || undefined} reasoningStarter={wp.reasoningStarter || undefined} width={Math.min(500, wsHeight * 1.35)} />; })()}
                      </div>
                    ) : wsKind === 't-chart' ? (
                      /* T-chart — column headers teacher-editable via wsProps */
                      <div className="flex flex-col gap-1 py-2">
                        {(() => { const base = parseTChartHint(p.prompt); const wp = p.wsProps ?? {}; return <TChartSVG {...base} leftLabel={wp.leftLabel || base.leftLabel} rightLabel={wp.rightLabel || base.rightLabel} studentMode width={Math.min(500, wsHeight * 2.5)} />; })()}
                      </div>
                    ) : wsKind === 'story-map' ? (
                      /* Story map — box labels teacher-editable via wsProps */
                      <div className="flex flex-col gap-1 py-2">
                        {(() => { const base = parseStoryMapHint(p.prompt); const wp = p.wsProps ?? {}; const boxLabels = ([0,1,2,3,4].map(i => wp[`boxLabel${i}`]).filter(Boolean) as string[]); return <StoryMapSVG {...base} boxLabels={boxLabels.length > 0 ? boxLabels : base.boxLabels} studentMode width={Math.min(520, wsHeight * 2.8)} />; })()}
                      </div>
                    ) : wsKind === 'main-idea-web' ? (
                      /* Main idea web — center topic teacher-editable via wsProps */
                      <div className="flex flex-col items-center gap-1 py-2">
                        {(() => { const wp = p.wsProps ?? {}; return <MainIdeaWebSVG studentMode detailCount={3} mainIdea={wp.mainIdea || undefined} width={Math.min(500, wsHeight * 1.35)} />; })()}
                      </div>
                    ) : wsKind === 'sound-boxes' ? (
                      /* Sound boxes (Elkonin) — word + box count teacher-editable via wsProps */
                      <div className="flex flex-col items-center gap-1 py-2">
                        {(() => { const base = parseSoundBoxesHint(p.prompt); const wp = p.wsProps ?? {}; return <SoundBoxesSVG {...base} word={wp.word || base.word} phonemeCount={wp.phonemeCount !== undefined ? parseInt(wp.phonemeCount, 10) : base.phonemeCount} studentMode width={Math.min(380, wsHeight * 2.5)} />; })()}
                      </div>
                    ) : wsKind === 'picture-letter' ? (
                      /* Picture-letter card */
                      <div className="flex flex-col items-center gap-1 py-2">
                        {(() => { const props = parsePictureLetterCardHint(p.prompt); return <PictureLetterCardSVG {...props} studentMode width={Math.min(300, wsHeight * 0.85)} />; })()}
                      </div>
                    ) : (
                      /* Computation box — border thickness is grade-aware (K-2: 2px, 3+: 1px) */
                      <div
                        className="as-workspace rounded-lg border border-dashed border-slate-200"
                        style={{
                          height: `${prefs.responseBoxHeight}px`,
                          backgroundImage: prefs.graphPaperAreas
                            ? 'linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)'
                            : undefined,
                          backgroundSize: prefs.graphPaperAreas ? '16px 16px' : undefined,
                          backgroundColor: 'rgba(248,250,252,0.2)',
                          borderWidth: `${workspaceBorder}px`,
                        }}
                      />
                    )
                  ) : null}
                  {/* Extra diagram space */}
                  {prefs.diagramSpaceHeight > 0 && (
                    <div
                      className="rounded-lg border border-dashed border-slate-200 flex items-end px-2 pb-1.5"
                      style={{ height: `${prefs.diagramSpaceHeight}px`, borderWidth: `${workspaceBorder}px` }}
                    >
                      <span className="text-[10px] text-slate-300 font-medium uppercase tracking-wide">Diagram Space</span>
                    </div>
                  )}
                </div>
              )}
              {/* Diagram label editor — shown in edit mode so teachers can customize SVG labels.
                  Works for all diagram workspace kinds that have user-editable label props. */}
              {showWorkspace && isEditing && onUpdateSection && wsKind
                && !['lines', 'computation', 'diagram', 'ten-frame', 'number-bond', 'picture-letter'].includes(wsKind) && (
                <DiagramLabelEditor
                  wsKind={wsKind}
                  wsProps={p.wsProps ?? {}}
                  onChange={newWsProps => setProblemWsProps(p.number, newWsProps)}
                  className="mx-1"
                />
              )}
              {/* In visual-dominant (K-1) mode, problem text follows the workspace */}
              {isVisualDominant && problemText}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeachingKeySteps({ answer }: { answer: string }) {
  const steps = answer.split(' → ');
  const final = steps[steps.length - 1];
  const intermediate = steps.slice(0, -1);
  return (
    <div className="border-l-2 border-emerald-200 pl-3 space-y-1">
      {intermediate.map((step, i) => (
        <div key={i} className="text-xs text-slate-500 font-mono leading-relaxed">{step}</div>
      ))}
      <div className="text-sm font-bold text-emerald-700">✓ {final}</div>
    </div>
  );
}

function AnswerKeyCard({
  section, isEditing, onUpdateSection,
}: {
  section: ActivitySection;
  isEditing?: boolean;
  onUpdateSection?: (updated: ActivitySection) => void;
}) {
  const baseHeading = section.heading.replace(' (Foundational)', '').replace(' (Enrichment)', '');
  const colorClass = SECTION_COLORS[baseHeading] ?? 'border-l-slate-400';
  const hasWorkedSteps = section.problems.some(p => p.answer.includes(' → '));

  function setAnswerForProblem(num: number, val: string) {
    onUpdateSection?.({
      ...section,
      problems: section.problems.map(p => p.number === num ? { ...p, answer: val } : p),
    });
  }

  return (
    <div className={`bg-green-50/50 rounded-2xl border border-green-100 border-l-4 ${colorClass} overflow-hidden`}>
      <div className="px-6 py-3 border-b border-green-100 flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">{section.heading}</h4>
        {hasWorkedSteps && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            Teaching Key
          </span>
        )}
        {isEditing && (
          <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full ml-2">
            Editing answers
          </span>
        )}
      </div>
      <div className="px-6 py-4 space-y-4">
        {section.problems.map(p => {
          const isWorked = p.answer.includes(' → ');
          const writingLabelMatch = p.prompt.match(
            /^(CLAIM|EVIDENCE|REASONING|RESTATE|ANSWER|CITE|EXPLAIN|SCENARIO|QUESTION|INTRODUCTION|BODY|CONCLUSION|ASSERT|SUPPORT|ANALYZE):\s*/i,
          );
          if (writingLabelMatch) {
            const label = writingLabelMatch[1].toUpperCase();
            return (
              <div key={p.number} className="space-y-0.5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
                {isEditing ? (
                  <textarea
                    value={p.answer}
                    onChange={e => setAnswerForProblem(p.number, e.target.value)}
                    rows={2}
                    className="w-full text-sm text-slate-700 leading-relaxed bg-amber-50 border border-amber-200 rounded px-2 py-1 resize-y focus:outline-none focus:ring-1 focus:ring-amber-300"
                  />
                ) : (
                  <p className="text-sm text-slate-700 leading-relaxed">{p.answer}</p>
                )}
              </div>
            );
          }
          return (
            <div key={p.number} className={isWorked ? 'space-y-1.5' : 'flex gap-3 text-sm'}>
              {isWorked ? (
                <>
                  <div className="flex gap-2">
                    <span className="font-bold text-slate-400 text-sm flex-shrink-0">{p.number}.</span>
                    <span className="text-slate-600 text-xs font-medium leading-relaxed pt-0.5">{p.prompt}</span>
                  </div>
                  <div className="ml-5">
                    {isEditing ? (
                      <textarea
                        value={p.answer}
                        onChange={e => setAnswerForProblem(p.number, e.target.value)}
                        rows={2}
                        className="w-full text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 resize-y focus:outline-none focus:ring-1 focus:ring-amber-300"
                      />
                    ) : (
                      <TeachingKeySteps answer={p.answer} />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <span className="font-bold text-slate-500 flex-shrink-0">{p.number}.</span>
                  {isEditing ? (
                    <textarea
                      value={p.answer}
                      onChange={e => setAnswerForProblem(p.number, e.target.value)}
                      rows={2}
                      className="flex-1 text-slate-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 resize-y focus:outline-none focus:ring-1 focus:ring-amber-300 text-sm"
                    />
                  ) : (
                    <span className="text-slate-700">{p.answer}</span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────
interface ActivitySheetProps {
  onBack: () => void;
  onGoMyResources: () => void;
  onNavigateTo: (section: string, ctx?: { fromLessonTitle?: string; fromLessonId?: string }) => void;
  lessonResource?: SavedResource | null;
  initialResource?: SavedResource | null;
}

type SheetView = 'config' | 'loading' | 'result' | 'incomplete';

function defaultConfig(title = ''): ActivityConfig {
  return {
    title,
    showStudentName: true,
    showDate: true,
    showLearningTarget: true,
    difficulty: 'on-level',
    problemCount: 10,
    includeVocabulary: true,
    includeWarmUp: true,
    includeGuidedPractice: true,
    includeIndependentPractice: true,
    includeChallengeProblem: true,
    includeExitTicket: true,
    includeAnswerKey: true,
    includeWorkSpace: true,
    tierSelection: undefined,
  };
}

// ── Main component ─────────────────────────────────────────────────────────────
export function ActivitySheet({ onBack, onGoMyResources, onNavigateTo, lessonResource, initialResource }: ActivitySheetProps) {
  const { saveResource, updateResource } = useResources();
  const { toasts, show: showToast } = useToasts();

  const lesson: GeneratedLesson | null = React.useMemo(() => {
    try {
      if (lessonResource?.lessonData) return JSON.parse(lessonResource.lessonData) as GeneratedLesson;
      return null;
    } catch { return null; }
  }, [lessonResource]);

  const savedSheet: GeneratedActivitySheet | null = React.useMemo(() => {
    try {
      if (initialResource?.lessonData) return JSON.parse(initialResource.lessonData) as GeneratedActivitySheet;
      return null;
    } catch { return null; }
  }, [initialResource]);

  const [view, setView] = useState<SheetView>(savedSheet ? 'result' : 'config');
  const [config, setConfig] = useState<ActivityConfig>(() =>
    savedSheet ? savedSheet.config : defaultConfig(lesson?.title ?? '')
  );
  const [sheet, setSheet] = useState<GeneratedActivitySheet | null>(savedSheet);
  const [savedResourceId, setSavedResourceId] = useState<string | null>(initialResource?.id ?? null);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editableSheet, setEditableSheet] = useState<GeneratedActivitySheet | null>(null);
  const { savedDefault: activityDefaultTheme, saveDefault: saveActivityDefault, restoreDefault: restoreActivityDefault } = useDefaultThemes('activity');
  // Restore theme from saved sheet; fall back to workspace default.
  const [sheetThemeId, setSheetThemeId] = useState<TeachBudThemeId>(
    (savedSheet?.sheetThemeId as TeachBudThemeId | undefined) ?? activityDefaultTheme,
  );

  // Apply profile theme default once when profile loads (new sheet only)
  const { profile: teacherProfile } = useProfile();
  const activityProfileDefaultsApplied = useRef(false);
  useEffect(() => {
    if (!teacherProfile || initialResource || activityProfileDefaultsApplied.current) return;
    activityProfileDefaultsApplied.current = true;
    if (teacherProfile.defaultActivityTheme) setSheetThemeId(teacherProfile.defaultActivityTheme as TeachBudThemeId);
  }, [teacherProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore formatting prefs from saved sheet; fall back to defaults.
  const [formatPrefs, setFormatPrefs] = useState<FormatPreferences>(
    savedSheet?.formatPrefs ?? DEFAULT_FORMAT,
  );
  // True when the teacher has explicitly saved custom prefs for this sheet.
  // When true, grade-band auto-defaults must not overwrite the teacher's choices.
  const prefsManuallyEdited = useRef<boolean>(!!savedSheet?.formatPrefs);

  // Auto-apply grade-band typography defaults when the sheet's grade changes (Spec §2).
  // sheet.grade is a string like "K" or "3" — parse it to a number first.
  // Skipped when the teacher has already saved custom prefs for this resource.
  React.useEffect(() => {
    if (!sheet?.grade) return;
    if (prefsManuallyEdited.current) return; // teacher has saved custom prefs — do not overwrite
    const overrides = getGradeBandOverrides(gradeNumFromLabel(sheet.grade));
    setFormatPrefs(prev => ({ ...prev, ...overrides }));
  }, [sheet?.grade]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showFormatPanel, setShowFormatPanel] = useState(false);
  const [showBrandingPanel, setShowBrandingPanel] = useState(false);
  // Restore branding and hidden sections from saved sheet.
  const [branding, setBranding] = useState<BrandingData>(
    (savedSheet?.branding as BrandingData | undefined) ?? defaultBranding(),
  );
  const [hiddenSections, setHiddenSections] = useState<string[]>(savedSheet?.hiddenSections ?? []);
  const [showTrace, setShowTrace] = useState(false);
  /** Lesson with one or more sections patched by targeted regeneration — overrides the source lesson. */
  const [patchedLesson, setPatchedLesson] = useState<GeneratedLesson | null>(null);
  /** Display name of the section currently being regenerated, or null when idle. */
  const [regenerating, setRegenerating] = useState<string | null>(null);
  /** Certification report — non-null while the gate modal is open. */
  const [certReport, setCertReport] = useState<CertificationReport | null>(null);
  /** The export action held while waiting for the teacher's decision in the gate modal. */
  const [pendingExportAction, setPendingExportAction] = useState<(() => void) | null>(null);
  /** Warning-only report shown in the inline banner after a bypass export. */
  const [certBypassWarning, setCertBypassWarning] = useState<CertificationReport | null>(null);

  // ── Auto-synchronization ────────────────────────────────────────────────────
  const prevLessonKeyRef = useRef<string | null>(null);
  const configRef = useRef(config);
  const sheetExistsRef = useRef(!!sheet);
  const viewRef = useRef<SheetView>(view);
  React.useEffect(() => { configRef.current = config; });
  React.useEffect(() => { sheetExistsRef.current = !!sheet; });
  React.useEffect(() => { viewRef.current = view; });

  React.useEffect(() => {
    if (!lesson) return;
    const key = lessonContentKey(lesson);
    const prev = prevLessonKeyRef.current;
    prevLessonKeyRef.current = key;
    if (prev === null || prev === key) return;
    // Source lesson changed (full regeneration) — discard any targeted patches
    setPatchedLesson(null);
    if (!sheetExistsRef.current || viewRef.current !== 'result') return;
    setView('loading');
    setTimeout(() => {
      const synced = generateActivitySheet(lesson, configRef.current);
      setSheet(synced);
      setSavedResourceId(null);
      setView(synced.incomplete ? 'incomplete' : 'result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 600);
  }, [lesson]); // eslint-disable-line react-hooks/exhaustive-deps

  function setConfigField<K extends keyof ActivityConfig>(key: K, value: ActivityConfig[K]) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  /**
   * Regenerates a single failed section without discarding the rest of the lesson.
   * Maps the activity-sheet display name to the API field key, calls the
   * /regenerate-section endpoint, patches the lesson, re-extracts the blueprint,
   * and rebuilds the activity sheet.
   */
  async function handleRegenerateSection(sectionDisplayName: string) {
    const effectiveLesson = patchedLesson ?? lesson;
    if (!effectiveLesson?.spec) return;

    const sectionKey = SECTION_KEY_MAP[sectionDisplayName];
    if (!sectionKey) return;

    setRegenerating(sectionDisplayName);
    try {
      const spec = effectiveLesson.spec;
      const currentContent: Record<string, unknown> = {
        warmUp:              effectiveLesson.warmUp,
        directInstruction:   effectiveLesson.directInstruction,
        guidedPractice:      effectiveLesson.guidedPractice,
        independentPractice: effectiveLesson.independentPractice,
        exitTicket:          effectiveLesson.exitTicket,
        tieredPractice:      effectiveLesson.tieredPractice,
      };

      const result = await regenerateSectionAI({
        skillToTeach:    spec.skillToTeach,
        grade:           spec.grade,
        subject:         spec.subject,
        standard:        spec.standard,
        duration:        spec.duration,
        instructions:    spec.instructions,
        requiredStrategy: spec.requiredStrategy,
        tieredPractice:  spec.tieredPractice,
        section:         sectionKey,
        reason:          `Section "${sectionDisplayName}" was empty or failed to produce parseable student problems`,
        currentContent,
      });

      // Build a patched copy of the lesson
      const newLesson: GeneratedLesson = { ...effectiveLesson };
      let patched = false;

      if (sectionKey === 'tieredPractice') {
        const tp = result['tieredPractice'] as Record<string, unknown> | undefined;
        if (tp && typeof tp === 'object') {
          const below  = String(tp['belowLevel'] ?? '').trim();
          const onLvl  = String(tp['onLevel']    ?? '').trim();
          const above  = String(tp['aboveLevel'] ?? '').trim();
          // Only accept if the AI actually returned content in at least one tier
          if (below.length > 10 || onLvl.length > 10 || above.length > 10) {
            newLesson.tieredPractice = {
              belowLevel: below  || effectiveLesson.tieredPractice?.belowLevel || '',
              onLevel:    onLvl  || effectiveLesson.tieredPractice?.onLevel    || '',
              aboveLevel: above  || effectiveLesson.tieredPractice?.aboveLevel || '',
            };
            patched = true;
          }
        }
      } else if (typeof result[sectionKey] === 'string') {
        const content = (result[sectionKey] as string).trim();
        if (content.length > 10) {
          (newLesson as unknown as Record<string, unknown>)[sectionKey] = content;
          patched = true;
        }
      }

      if (!patched) {
        // The AI returned nothing usable — don't silently rebuild from the same broken content
        console.error('[TeachBud] Regeneration returned no usable content for section:', sectionKey, result);
        showToast(`Regeneration did not produce valid content for "${sectionDisplayName}" — please try again.`);
        return;
      }

      // Re-extract blueprint so the activity sheet sees the updated content
      newLesson.blueprint = extractBlueprint(newLesson);
      setPatchedLesson(newLesson);

      // Rebuild the activity sheet from the patched lesson
      setView('loading');
      setTimeout(() => {
        const generated = generateActivitySheet(newLesson, configRef.current);
        setSheet(generated);
        setSavedResourceId(null);
        setView(generated.incomplete ? 'incomplete' : 'result');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 600);
    } catch (err) {
      console.error('[TeachBud] Section regeneration failed:', err);
      showToast('Regeneration failed — please try again.');
    } finally {
      setRegenerating(null);
    }
  }

  function handleGenerate() {
    const effectiveLesson = patchedLesson ?? lesson;
    if (!effectiveLesson) return;

    // ── Blueprint Completeness Gate ───────────────────────────────────────────
    // Refuse to render from an incomplete blueprint. An incomplete blueprint
    // produces a nearly-empty worksheet — synchronized emptiness. The teacher
    // needs to regenerate the lesson before any companion resource can be created.
    const bpForCheck = effectiveLesson.blueprint ?? extractBlueprint(effectiveLesson);
    const completeness = validateBlueprintCompleteness(bpForCheck);
    if (!completeness.complete) {
      const missingList = completeness.missingFields
        .map(f => `${f} (${completeness.reasons[f]})`)
        .join('; ');
      showToast(
        `Lesson blueprint is incomplete — missing: ${missingList}. ` +
        `Return to the lesson and regenerate it before creating an activity sheet.`,
        'error'
      );
      return;
    }

    setView('loading');
    setTimeout(() => {
      const generated = generateActivitySheet(effectiveLesson, config);
      setSheet(generated);
      setSavedResourceId(null);
      // An incomplete sheet is not a complete resource. Route to the error
      // view so the teacher knows to return to the Lesson Planner and regenerate.
      setView(generated.incomplete ? 'incomplete' : 'result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 900);
  }

  function handleCopy() {
    if (!sheet) return;
    navigator.clipboard.writeText(activityToText(sheet)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast('Copied to clipboard!');
  }

  function handleSave() {
    if (!sheet) return;
    const text = activityToText(sheet);
    // Embed UI state (formatPrefs, branding, sheetThemeId, hiddenSections) into the
    // persisted JSON so every teacher edit survives close/reopen/export/regeneration.
    prefsManuallyEdited.current = true; // lock grade-band defaults now that prefs are saved
    const sheetToSave: GeneratedActivitySheet = {
      ...sheet,
      formatPrefs,
      branding,
      sheetThemeId,
      hiddenSections,
    };
    const resourceData = {
      type: 'activity' as const,
      title: sheet.title,
      grade: sheet.grade,
      subject: sheet.subject,
      topic: sheet.topic,
      standard: sheet.standard,
      plainText: text,
      lessonData: JSON.stringify(sheetToSave),
      renderingSpecVersion: REGISTRY_VERSION,
    };
    if (savedResourceId) {
      updateResource(savedResourceId, { ...resourceData, lastEditedAt: new Date().toISOString() });
      showToast('Activity sheet updated!');
    } else {
      const id = saveResource(resourceData);
      setSavedResourceId(id);
      showToast('Saved to My Resources!');
    }
  }

  function doPrint(isBypass = false) {
    const styleId = '__tb-print-orient';
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = `@media print { @page { size: ${formatPrefs.orientation}; margin: 0.75in; } }`;
    applyPrintBypass(() => window.print(), isBypass);
  }

  function handlePrint() {
    if (!sheet) return;
    // ── Teacher-Ready Certification: run the full check suite before print ──
    const blueprint = lesson?.blueprint ?? (lesson ? extractBlueprint(lesson) : undefined);
    const report = runCertification({
      resource: 'activity-sheet',
      sheet,
      lesson: lesson ?? undefined,
      blueprint,
    });
    if (!report.passed || report.hasWarnings) {
      // Hold the print action and surface the certification modal
      setCertReport(report);
      setPendingExportAction(() => () => {
        if (report.hasWarnings && report.passed) setCertBypassWarning(report);
        doPrint(true); // bypass — watermark applied
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    doPrint();
  }

  function handleStartEdit() {
    setEditTitle(sheet?.title ?? '');
    setEditableSheet(sheet ? { ...sheet } : null);
    setIsEditing(true);
  }

  function handleFinishEdit() {
    const committed = editableSheet ?? sheet;
    if (committed) {
      setSheet({ ...committed, title: editTitle.trim() || committed.title });
    }
    setIsEditing(false);
    setEditableSheet(null);
  }

  function updateSection(updated: ActivitySection) {
    setEditableSheet(prev => {
      if (!prev) return prev;
      // Match using sectionKind (stable, survives heading renames) with heading as fallback.
      // This preserves color/icon lookup and linked-resource routing after a rename.
      const key = updated.sectionKind ?? updated.heading;
      const match = (s: ActivitySection | undefined) => {
        if (!s) return s;
        const sKey = s.sectionKind ?? s.heading;
        return sKey === key ? updated : s;
      };
      return {
        ...prev,
        warmUp:              match(prev.warmUp),
        vocabularyActivity:  match(prev.vocabularyActivity),
        directInstruction:   match(prev.directInstruction),
        guidedPractice:      match(prev.guidedPractice),
        independentPractice: match(prev.independentPractice),
        belowLevelPractice:  match(prev.belowLevelPractice),
        onLevelPractice:     match(prev.onLevelPractice),
        aboveLevelPractice:  match(prev.aboveLevelPractice),
        challengeProblem:    match(prev.challengeProblem),
        exitTicket:          match(prev.exitTicket),
      };
    });
  }

  function handleStartNew() {
    setSheet(null);
    setSavedResourceId(null);
    setIsEditing(false);
    setConfig(defaultConfig(lesson?.title ?? ''));
    setView('config');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleNavigate(section: string, label: string) {
    if (!sheet) return;
    let resId = savedResourceId;
    if (!resId) {
      resId = saveResource({
        type: 'activity',
        title: sheet.title,
        grade: sheet.grade,
        subject: sheet.subject,
        topic: sheet.topic,
        standard: sheet.standard,
        plainText: activityToText(sheet),
        lessonData: JSON.stringify(sheet),
        lessonId: lessonResource?.id,
        renderingSpecVersion: REGISTRY_VERSION,
      });
      setSavedResourceId(resId);
      showToast(`Activity saved. Opening ${label}…`);
    }
    // Pass the LESSON resource id, not the activity resource id.
    // PresentationGenerator and AssessmentGenerator receive lessonResource from App.tsx
    // by looking up fromLessonId — they need a lesson (type:'lesson'), not this activity.
    const lessonId = lessonResource?.id ?? undefined;
    setTimeout(() => {
      onNavigateTo(section, { fromLessonTitle: sheet.title, fromLessonId: lessonId });
    }, 600);
  }

  // ── Loading view ────────────────────────────────────────────────────────────
  if (view === 'loading') {
    return <GenerationProgress resourceType="activity" />;
  }

  // ── Incomplete view — AI section parse failure; not a complete resource ────────
  if (view === 'incomplete' && sheet) {
    const effectiveLessonForRegen = patchedLesson ?? lesson;
    const canRegenerate = !!effectiveLessonForRegen?.spec;
    return (
      <motion.div key="incomplete" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl mx-auto">
        <ToastContainer toasts={toasts} />
        <div className="bg-red-50 border border-red-200 rounded-2xl p-10 flex flex-col items-center text-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
            <AlertCircle size={28} className="text-red-500" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Activity Sheet Incomplete</h3>
            <p className="text-slate-600 text-sm leading-relaxed max-w-sm mx-auto">
              TeachBud could not extract complete student problems from the
              {sheet.failedSections.length === 1 ? ' section' : ' sections'} listed below.
              {canRegenerate
                ? ' Use the button below to regenerate just the failed section — no need to restart.'
                : ' Return to the Lesson Planner and regenerate the lesson to try again.'}
            </p>
          </div>
          {sheet.failedSections.length > 0 && (
            <div className="bg-white rounded-xl border border-red-100 px-5 py-4 text-left w-full max-w-sm">
              <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-3">
                {sheet.failedSections.length === 1 ? 'Affected Section' : `Affected Sections (${sheet.failedSections.length})`}
              </p>
              <ul className="space-y-4">
                {sheet.failedSections.map(f => (
                  <li key={f.name} className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <XCircle size={14} className="text-red-400 flex-shrink-0" />
                      <span className="text-sm font-bold text-slate-900">{f.name}</span>
                    </div>
                    <p className="text-xs text-slate-500 pl-6">{f.reason}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            {/* Per-section regenerate buttons — one per failed section that has a known field mapping */}
            {canRegenerate && sheet.failedSections
              .filter(f => !!SECTION_KEY_MAP[f.name])
              .map(f => {
                const isRegenerating = regenerating === f.name;
                return (
                  <Button key={f.name}
                    onClick={() => { void handleRegenerateSection(f.name); }}
                    disabled={!!regenerating}
                    className="rounded-xl gap-1.5 bg-primary hover:bg-primary/90 text-white">
                    {isRegenerating
                      ? <Loader2 size={14} className="animate-spin" />
                      : <RotateCcw size={14} />}
                    Regenerate {f.name}
                  </Button>
                );
              })}
            <Button variant="outline" onClick={onBack}
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 gap-1.5">
              <ChevronLeft size={14} /> Return to Lesson Planner
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Config view ─────────────────────────────────────────────────────────────
  if (view === 'config') {
    const hasLesson = !!lesson;
    return (
      <motion.div key="config" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl mx-auto space-y-6">
        <ToastContainer toasts={toasts} />

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <FileText size={18} className="text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">Activity Sheet</h2>
              {lesson && <p className="text-xs text-slate-500">From: {lesson.title}</p>}
            </div>
          </div>
        </div>

        {/* Lesson banner */}
        {lesson && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-violet-50 border border-violet-100 rounded-2xl px-5 py-3.5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-violet-900">Creating Activity Sheet from:</p>
              <p className="text-sm text-violet-700 font-bold">{lesson.title}</p>
              <p className="text-xs text-violet-500">{lesson.gradeLabel} · {lesson.subject} · {lesson.topic}</p>
            </div>
          </motion.div>
        )}

        {!hasLesson && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              No lesson data found. Please generate a lesson first using the <strong>Lesson Planner</strong>, then click <em>Create Activity Sheet</em>.
            </p>
          </div>
        )}

        {/* Config panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50">

          {/* Title */}
          <div className="px-6 py-5 space-y-2">
            <Label htmlFor="as-title" className="text-sm font-semibold text-slate-700">Activity Sheet Title</Label>
            <Input
              id="as-title"
              value={config.title}
              onChange={e => setConfigField('title', e.target.value)}
              placeholder={lesson?.title ?? 'Activity Sheet Title'}
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
          </div>

          {/* Header toggles */}
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm font-semibold text-slate-700">Worksheet Header</p>
            <div className="space-y-3">
              {([
                ['showStudentName', 'Student Name line'] as const,
                ['showDate', 'Date line'] as const,
                ['showLearningTarget', 'Learning Target'] as const,
              ]).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <Label htmlFor={`toggle-${key}`} className="text-sm text-slate-600 cursor-pointer">{label}</Label>
                  <Toggle id={`toggle-${key}`} checked={config[key]} onChange={v => setConfigField(key, v)} />
                </div>
              ))}
            </div>
          </div>

          {/* Difficulty */}
          <div className="px-6 py-5 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Difficulty</p>
            <div className="flex gap-2 flex-wrap">
              {([
                ['easy', 'Easy'],
                ['on-level', 'On Level'],
                ['challenge', 'Challenge'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setConfigField('difficulty', value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    config.difficulty === value
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Problem count */}
          <div className="px-6 py-5 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Number of Practice Problems</p>
            <div className="flex gap-2 flex-wrap">
              {([5, 10, 15, 20] as const).map(n => (
                <button
                  key={n}
                  onClick={() => setConfigField('problemCount', n)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all min-w-[52px] ${
                    config.problemCount === n
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Include sections */}
          <div className="px-6 py-5 space-y-1">
            <p className="text-sm font-semibold text-slate-700 mb-2">Include</p>
            <CheckItem label="☀️ Warm-Up" checked={config.includeWarmUp} onChange={v => setConfigField('includeWarmUp', v)} />
            <CheckItem label="📚 Vocabulary" checked={config.includeVocabulary} onChange={v => setConfigField('includeVocabulary', v)} />
            <CheckItem label="🤝 Guided Practice" checked={config.includeGuidedPractice} onChange={v => setConfigField('includeGuidedPractice', v)} />
            <CheckItem label="✏️ Independent Practice" checked={config.includeIndependentPractice} onChange={v => setConfigField('includeIndependentPractice', v)} />
            <CheckItem label="⭐ Challenge Problem" checked={config.includeChallengeProblem} onChange={v => setConfigField('includeChallengeProblem', v)} />
            <CheckItem label="🎯 Exit Ticket" checked={config.includeExitTicket} onChange={v => setConfigField('includeExitTicket', v)} />
            <div className="pt-2 border-t border-slate-50 mt-2">
              <CheckItem label="🔑 Answer Key (Teacher Copy)" checked={config.includeAnswerKey} onChange={v => setConfigField('includeAnswerKey', v)} />
            </div>
          </div>

          {/* Differentiated tier selector — shown only when the lesson has tiered practice */}
          {lesson?.tieredPractice && (
            <div className="px-6 py-5 space-y-3 border-t border-slate-100">
              <div>
                <p className="text-sm font-semibold text-slate-700">Differentiated Practice</p>
                <p className="text-xs text-slate-400 mt-0.5">This lesson includes tiered independent practice. Choose which tier to print.</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {([
                  [undefined,    'Standard'],
                  ['below',      'Below Level'],
                  ['on',         'On Level'],
                  ['above',      'Above Level'],
                  ['combined',   'All Tiers'],
                ] as const).map(([value, label]) => (
                  <button
                    key={String(value)}
                    onClick={() => setConfigField('tierSelection', value)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                      config.tierSelection === value
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Generate button */}
        <Button
          onClick={handleGenerate}
          disabled={!hasLesson}
          className="w-full h-12 rounded-xl text-base font-semibold bg-primary hover:bg-primary/90 text-white shadow-sm disabled:opacity-50"
        >
          <FileText size={18} className="mr-2" />
          Generate Activity Sheet
        </Button>
      </motion.div>
    );
  }

  // ── Result view ─────────────────────────────────────────────────────────────
  if (!sheet) return null;

  // Resolve which independent practice section(s) to display based on tierSelection
  const ipSectionsForDisplay: (ActivitySection | undefined)[] = [];
  if (sheet.config.tierSelection === 'below') {
    ipSectionsForDisplay.push(sheet.belowLevelPractice);
  } else if (sheet.config.tierSelection === 'on') {
    ipSectionsForDisplay.push(sheet.onLevelPractice);
  } else if (sheet.config.tierSelection === 'above') {
    ipSectionsForDisplay.push(sheet.aboveLevelPractice);
  } else if (sheet.config.tierSelection === 'combined') {
    ipSectionsForDisplay.push(sheet.belowLevelPractice, sheet.onLevelPractice, sheet.aboveLevelPractice);
  } else {
    ipSectionsForDisplay.push(sheet.independentPractice);
  }

  const sections = [
    sheet.warmUp,
    sheet.vocabularyActivity,
    // NON-NEGOTIABLE: Direct Instruction guided notes come between Vocabulary and
    // Guided Practice so students can follow along during the teacher's modeled examples.
    sheet.directInstruction,
    sheet.guidedPractice,
    ...ipSectionsForDisplay,
    sheet.challengeProblem,
    sheet.exitTicket,
  ].filter((s): s is ActivitySection => s !== undefined);

  const sheetFontFamily = formatPrefs.fontFamily === 'serif'
    ? 'Georgia, "Times New Roman", serif'
    : 'var(--app-font-sans, system-ui, sans-serif)';

  return (
    <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-3xl mx-auto space-y-4">
      <ToastContainer toasts={toasts} />

      {/* ── Certification bypass warning banner (warn-only advisories after export) ── */}
      <CertificationWarningBanner
        report={certBypassWarning}
        onDismiss={() => setCertBypassWarning(null)}
      />

      {/* ── Certification gate modal — shown before print when checks fail ── */}
      {certReport && (
        <CertificationGate
          report={certReport}
          resourceLabel="Activity Sheet"
          onExportAnyway={() => {
            const action = pendingExportAction;
            setCertReport(null);
            setPendingExportAction(null);
            action?.();
          }}
          onClose={() => {
            setCertReport(null);
            setPendingExportAction(null);
          }}
        />
      )}

      {/* ── Top toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={onBack}
          className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
          <ChevronLeft size={15} /> Back
        </Button>
        {!isEditing ? (
          <Button variant="outline" size="sm" onClick={handleStartEdit}
            className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
            <Edit3 size={14} /> Edit
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={handleFinishEdit}
            className="rounded-xl border-green-200 bg-green-50 text-green-700 hover:bg-green-100 gap-1.5">
            <CheckCircle2 size={14} /> Done Editing
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={handleCopy}
          className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
          {copied ? <CheckCheck size={15} className="text-green-500" /> : <Copy size={15} />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <ThemePicker
          value={sheetThemeId} onChange={setSheetThemeId} compact label="Theme"
          resourceType="activity"
          onSaveDefault={() => saveActivityDefault(sheetThemeId)}
          onRestoreDefault={() => setSheetThemeId(restoreActivityDefault())}
        />
        <Button variant="outline" size="sm" onClick={() => setShowBrandingPanel(v => !v)}
          className={`rounded-xl gap-1.5 transition-all ${showBrandingPanel ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
          <Building2 size={14} /> School
        </Button>
        {/* Format toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFormatPanel(v => !v)}
          className={`rounded-xl gap-1.5 transition-all ${
            showFormatPanel
              ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
              : 'border-slate-200 hover:border-slate-300 text-slate-600'
          }`}
        >
          <Settings2 size={14} />
          Format
          {showFormatPanel ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </Button>
        <Button variant="outline" size="sm" onClick={handlePrint}
          className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
          <Printer size={15} /> Print
        </Button>
      </div>

      {/* ── Formatting panel (collapsible) ── */}
      <AnimatePresence>
        {showFormatPanel && (
          <FormattingPanel prefs={formatPrefs} onChange={setFormatPrefs} />
        )}
      </AnimatePresence>

      {/* ── Branding panel (collapsible) ── */}
      <BrandingPanel
        branding={branding}
        onChange={setBranding}
        open={showBrandingPanel}
        onToggle={() => setShowBrandingPanel(v => !v)}
      />

      {/* ── Page numbering CSS (print-only, @page margin-box) ── */}
      {formatPrefs.pageNumberStyle !== 'none' && (
        <style dangerouslySetInnerHTML={{ __html:
          formatPrefs.pageNumberStyle === 'page-x-of-y'
            ? `@page { @bottom-center { content: "Page " counter(page) " of " counter(pages); font-family: system-ui,sans-serif; font-size: 9pt; color: #64748b; } }`
            : `@page { @bottom-center { content: "Page " counter(page); font-family: system-ui,sans-serif; font-size: 9pt; color: #64748b; } }`
        }} />
      )}

      {/* ── Edit banner ── */}
      {isEditing && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-3 flex items-center gap-3 print:hidden">
          <Pencil size={16} className="text-amber-500 flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-xs font-semibold text-amber-700">Edit mode — update the title, section instructions, and problem text below.</p>
            <Input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              placeholder="Activity Sheet Title"
              className="h-9 text-sm rounded-lg border-amber-200 focus-visible:ring-amber-200 bg-white"
            />
          </div>
          <Button size="sm" onClick={handleFinishEdit}
            className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white gap-1.5 flex-shrink-0">
            <CheckCircle2 size={14} /> Done
          </Button>
        </motion.div>
      )}

      {/* ── Sheet header (printable) ── */}
      {(() => {
        const sheetTheme = getTheme(sheetThemeId);
        return (
      <div className="as-sheet-section bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="px-6 py-5"
          style={{ background: `linear-gradient(135deg, ${sheetTheme.headerBg1} 0%, ${sheetTheme.headerBg2} 100%)` }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p
                className="text-xs font-medium uppercase tracking-wide mb-1"
                style={{ fontFamily: sheetFontFamily, color: `${sheetTheme.headerText}cc` }}
              >
                Activity Sheet · {sheet.gradeLabel} · {sheet.subject}
              </p>
              <h1
                style={{ fontSize: `${formatPrefs.fontSize + 7}px`, fontFamily: sheetFontFamily, lineHeight: '1.2', color: sheetTheme.headerText }}
                className="font-extrabold"
              >
                {sheet.title}
              </h1>
              <p
                style={{ fontSize: `${formatPrefs.fontSize - 1}px`, fontFamily: sheetFontFamily, color: `${sheetTheme.headerText}cc` }}
                className="mt-1"
              >
                Topic: {sheet.topic}
              </p>
            </div>
            <div className="flex-shrink-0 hidden sm:flex items-center justify-center w-12 h-12 rounded-xl bg-white/20">
              <FileText size={24} style={{ color: sheetTheme.headerText }} />
            </div>
          </div>
          <BrandingHeaderRow branding={branding} textColor={sheetTheme.headerText} />
        </div>

        {/* Header fields */}
        {(sheet.config.showStudentName || sheet.config.showDate) && (
          <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap gap-x-12 gap-y-3">
            {sheet.config.showStudentName && (
              <p style={{ fontSize: `${formatPrefs.fontSize}px`, fontFamily: sheetFontFamily }} className="font-medium text-slate-700">
                Student Name: <span className="inline-block w-48 border-b border-slate-400 ml-2">&nbsp;</span>
              </p>
            )}
            {sheet.config.showDate && (
              <p style={{ fontSize: `${formatPrefs.fontSize}px`, fontFamily: sheetFontFamily }} className="font-medium text-slate-700">
                Date: <span className="inline-block w-32 border-b border-slate-400 ml-2">&nbsp;</span>
              </p>
            )}
            {sheet.standard && (
              <p className="text-xs text-slate-400">Standard: {sheet.standard}</p>
            )}
          </div>
        )}

        {/* Learning target */}
        {sheet.config.showLearningTarget && (
          <div className="px-6 py-4 border-b" style={{ backgroundColor: `${getTheme(sheetThemeId).accentBg}`, borderColor: `${getTheme(sheetThemeId).accentColor}30` }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: getTheme(sheetThemeId).accentColor }}>Learning Target</p>
            {isEditing ? (
              <input
                type="text"
                aria-label="Learning target"
                value={editableSheet?.learningTarget ?? sheet.learningTarget ?? ''}
                onChange={e => setEditableSheet(prev => prev ? { ...prev, learningTarget: e.target.value } : prev)}
                style={{ fontSize: `${formatPrefs.fontSize}px`, fontFamily: sheetFontFamily }}
                className="w-full text-slate-800 font-medium bg-transparent border-b border-amber-200 focus:outline-none focus:border-amber-400 py-0.5 print:hidden"
                placeholder="Learning target..."
              />
            ) : (
              <p
                style={{ fontSize: `${formatPrefs.fontSize}px`, fontFamily: sheetFontFamily }}
                className="text-slate-800 font-medium"
              >
                {sheet.learningTarget}
              </p>
            )}
          </div>
        )}
      </div>
        )
      })()}

      {/* ── Student sections ── */}
      {(() => {
        const sheetTheme = getTheme(sheetThemeId);
        const displaySections = (isEditing && editableSheet)
          ? [
              editableSheet.warmUp,
              editableSheet.vocabularyActivity,
              editableSheet.directInstruction,
              editableSheet.guidedPractice,
              ...(sheet.config.tierSelection === 'below'    ? [editableSheet.belowLevelPractice]
                : sheet.config.tierSelection === 'on'      ? [editableSheet.onLevelPractice]
                : sheet.config.tierSelection === 'above'   ? [editableSheet.aboveLevelPractice]
                : sheet.config.tierSelection === 'combined'? [editableSheet.belowLevelPractice, editableSheet.onLevelPractice, editableSheet.aboveLevelPractice]
                : [editableSheet.independentPractice]),
              editableSheet.challengeProblem,
              editableSheet.exitTicket,
            ].filter((s): s is ActivitySection => s !== undefined)
          : sections;
        return displaySections.map((section, idx) => {
          const baseHeading = section.heading.replace(' (Foundational)', '').replace(' (Enrichment)', '');
          const accentIdx = SECTION_ACCENT_INDEX[baseHeading] ?? idx;
          const accentHex = sheetThemeId === 'modern' ? undefined : sheetTheme.sectionAccents[accentIdx % sheetTheme.sectionAccents.length];
          const isHidden = hiddenSections.includes(section.heading);
          return (
            <SectionCard
              key={section.heading}
              section={section}
              prefs={formatPrefs}
              themeAccentHex={accentHex}
              isEditing={isEditing}
              onUpdateSection={isEditing ? updateSection : undefined}
              isHidden={isHidden}
              gradeNum={gradeNumFromLabel(sheet.grade)}
              onToggleHidden={() => setHiddenSections(prev =>
                prev.includes(section.heading)
                  ? prev.filter(h => h !== section.heading)
                  : [...prev, section.heading]
              )}
            />
          );
        });
      })()}

      {/* ── Answer Key ── */}
      {/* Note: no forced page-break here — forcing break-before-page causes a blank
          page when the final student section happens to end near a page boundary.
          The Answer Key naturally flows to the next page (or starts one if there
          is enough surrounding whitespace), which is the correct behavior. */}
      {sheet.config.includeAnswerKey && sections.length > 0 && (
        <div className="mt-2">
          <div className="bg-white rounded-2xl border border-green-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700">
              <p className="text-emerald-100 text-xs font-bold uppercase tracking-wide">TEACHER COPY</p>
              <h2 className="text-lg font-extrabold text-white mt-0.5">Answer Key</h2>
              <p className="text-emerald-100 text-xs mt-1">{sheet.title} · {sheet.gradeLabel} · {sheet.subject}</p>
            </div>
            <div className="p-6 space-y-4">
              {sections.map(section => (
                <AnswerKeyCard
                  key={section.heading}
                  section={section}
                  isEditing={isEditing}
                  onUpdateSection={isEditing ? updateSection : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Generation Report (developer panel — AI lessons only) ── */}
      {lesson?.trace && (
        <div className="print:hidden flex flex-col items-center gap-2">
          <button
            onClick={() => setShowTrace(v => !v)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Info size={12} />
            Generation Report
            {showTrace ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <AnimatePresence>
            {showTrace && (
              <motion.div
                key="trace"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="w-full bg-slate-900 rounded-2xl p-5 text-xs font-mono text-slate-300 space-y-3 overflow-hidden"
              >
                {/* ── Lesson origin ── */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Lesson Source</span>
                  <span className="text-emerald-400">{lesson.trace.lessonSource}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">LessonSpec</span>
                  <span className={lesson.trace.specPresent ? 'text-emerald-400' : 'text-red-400'}>
                    {lesson.trace.specPresent ? 'present' : 'absent'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Blueprint</span>
                  <span className={lesson.trace.blueprintExtracted ? 'text-emerald-400' : 'text-amber-400'}>
                    {lesson.trace.blueprintExtracted
                      ? `extracted · ${lesson.trace.blueprintItemCount} item${lesson.trace.blueprintItemCount !== 1 ? 's' : ''}`
                      : 'not extracted'}
                  </span>
                </div>
                {/* ── Validation ── */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Validation</span>
                  <span className={
                    lesson.trace.validationResult === 'passed'    ? 'text-emerald-400' :
                    lesson.trace.validationResult === 'corrected' ? 'text-amber-400'   :
                    lesson.trace.validationResult === 'failed'    ? 'text-red-400'     :
                    'text-slate-500'
                  }>{lesson.trace.validationResult ?? 'not run'}</span>
                </div>
                {(lesson.trace.sectionsRegenerated ?? []).length > 0 && (
                  <div className="flex items-start justify-between gap-4">
                    <span className="text-slate-500 uppercase tracking-wide text-[10px] flex-shrink-0">Sections Regenerated</span>
                    <span className="text-amber-400 text-right">{lesson.trace.sectionsRegenerated!.join(', ')}</span>
                  </div>
                )}
                {/* ── Activity sheet provenance ── */}
                <div>
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Activity Sheet Source</span>
                  <div className="mt-2 space-y-1.5">
                    {sections.map(s => (
                      <div key={s.heading} className="flex items-start justify-between gap-4">
                        <span className="text-slate-400 flex-shrink-0">{s.heading}</span>
                        <span className={
                          s.source === 'blueprint' ? 'text-emerald-400' :
                          s.source === 'raw-ai'    ? 'text-sky-400'     :
                          s.source === 'legacy'    ? 'text-amber-400'   :
                                                     'text-red-400'
                        }>
                          {s.source}
                          {s.source === 'failed' && s.failureReason
                            ? ` — ${s.failureReason}`
                            : ''}
                        </span>
                      </div>
                    ))}
                    {sheet.vocabularyActivity && (
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-400 flex-shrink-0">Vocabulary</span>
                        <span className="text-sky-400">{sheet.vocabularyActivity.source}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* ── Safeguards ── */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Legacy Generator</span>
                  <span className={sheet.incomplete ? 'text-red-400' : 'text-emerald-400'}>
                    {sheet.incomplete ? 'sections failed' : 'not used'}
                  </span>
                </div>
                {/* ── Timing ── */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 uppercase tracking-wide text-[10px]">Generation Time</span>
                  <span className="text-slate-400">
                    {lesson.trace.generationTimeMs != null
                      ? `${(lesson.trace.generationTimeMs / 1000).toFixed(1)} s`
                      : 'not recorded'}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ── Bottom toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 py-2 print:hidden">
        <Button variant="outline" size="sm"
          onClick={() => handleNavigate('assessments', 'Assessment')}
          className="rounded-xl border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600 gap-1.5">
          <ClipboardList size={14} /> Create Assessment
        </Button>
        <Button variant="outline" size="sm"
          onClick={() => handleNavigate('presentations', 'Presentation')}
          className="rounded-xl border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-600 gap-1.5">
          <Monitor size={14} /> Create Presentation
        </Button>
        <Button variant="outline" size="sm" onClick={handleSave}
          className={`rounded-xl gap-1.5 ${savedResourceId
            ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
            : 'border-slate-200 hover:border-slate-300 text-slate-600'
          }`}>
          {savedResourceId ? <CheckCircle2 size={15} /> : <Save size={15} />}
          {savedResourceId ? 'Saved' : 'Save to My Resources'}
        </Button>
        <SaveStatusIndicator resourceId={savedResourceId} />
        <Button variant="outline" size="sm" onClick={handleStartNew}
          className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
          <PlusCircle size={15} /> Start New Activity Sheet
        </Button>
      </div>
    </motion.div>
  );
}
